import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Combobox } from '../../../components/ui/combobox';
import {
    Branch,
    BranchContact,
    Customer,
    Project,
    ProjectPayload,
} from '../../../types/lists.types';
import { projectsApi } from '../../../utils/lists-api';
import { Yard } from '../../../types/stock.types';
import { yardsApi } from '../../../utils/stock-api';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    project: Project | null;
    /** Active customers only — inactive records are not selectable. */
    customers: Customer[];
    /** Every user, deliberately unfiltered by role. */
    users: BranchContact[];
    /** Active branches only. */
    branches: Branch[];
    onSaved: () => void;
}

const EMPTY: ProjectPayload = {
    projectName: '',
    projectNumber: '',
    siteAddress: '',
    customerId: null,
    projectManagerId: null,
    branchId: null,
    yardId: null,
};

export const ProjectFormDialog: React.FC<Props> = ({
    open,
    onOpenChange,
    project,
    customers,
    users,
    branches,
    onSaved,
}) => {
    const [form, setForm] = useState<ProjectPayload>(EMPTY);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Only OPEN yards. A closed yard is read-only and excluded from every selector
     * (STOCK.md §9, §10) — the server rejects one anyway, this stops it being offered.
     */
    const [yardOptions, setYardOptions] = useState<Yard[]>([]);

    const customerOptions = Array.isArray(customers) ? customers : [];
    const userOptions = Array.isArray(users) ? users : [];
    const branchOptions = Array.isArray(branches) ? branches : [];

    useEffect(() => {
        if (!open) return;
        if (project) {
            setForm({
                projectName: project.projectName,
                projectNumber: project.projectNumber,
                siteAddress: project.siteAddress,
                customerId: project.customer?.id ?? null,
                projectManagerId: project.projectManager?.id ?? null,
                branchId: project.branch?.id ?? null,
                yardId: project.yard?.id ?? null,
            });
        } else {
            setForm(EMPTY);
        }
        setErrors({});
    }, [open, project]);

    useEffect(() => {
        if (!open) return;
        yardsApi.list({ status: 'open' }).then(setYardOptions).catch(() => undefined);
    }, [open]);

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.projectName.trim()) next.projectName = 'Project Name is required';
        if (!form.projectNumber.trim()) next.projectNumber = 'Project Number is required';
        if (!form.siteAddress.trim()) next.siteAddress = 'Site Address is required';
        if (!form.customerId) next.customerId = 'Customer is required';
        if (!form.projectManagerId) next.projectManagerId = 'Project Manager is required';
        if (!form.branchId) next.branchId = 'Branch is required';
        if (!form.yardId) next.yardId = 'Yard is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setSaving(true);
            if (project) {
                await projectsApi.update(project.id, form);
                toast.success('Project updated');
            } else {
                await projectsApi.create(form);
                toast.success('Project created');
            }
            onSaved();
            onOpenChange(false);
        } catch (err: any) {
            // A duplicate Project Number comes back as 409 with the offending field
            // named, so pin the message to the input. api.ts has already toasted it.
            const field = err?.data?.field;
            const message = err?.data?.message || err?.message || 'Failed to save project';
            if (field) setErrors((prev) => ({ ...prev, [field]: message }));
        } finally {
            setSaving(false);
        }
    };

    const comboboxField = (
        key: 'customerId' | 'projectManagerId' | 'branchId' | 'yardId',
        label: string,
        options: { id: number; label: string }[],
        emptyText: string
    ) => (
        <div className="space-y-2">
            <Label htmlFor={`project-${key}`}>
                {label} <span className="text-destructive">*</span>
            </Label>
            <Combobox
                id={`project-${key}`}
                value={form[key] ? String(form[key]) : undefined}
                onChange={(v) =>
                    setForm((f) => ({ ...f, [key]: v ? Number(v) : null }))
                }
                options={options.map((o) => ({ value: String(o.id), label: o.label }))}
                placeholder={`Select a ${label.toLowerCase()}`}
                searchPlaceholder={`Search ${label.toLowerCase()}...`}
                emptyMessage={emptyText}
                className="w-full bg-white"
            />
            {errors[key] && <p className="text-xs text-destructive">{errors[key]}</p>}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-lg font-medium">
                        {project ? 'Edit Project' : 'Add Project'}
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        Projects are assigned to a Branch. Only Active customers and branches are selectable.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="py-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="project-projectName">
                                    Project Name <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="project-projectName"
                                    value={form.projectName}
                                    onChange={(e) => setForm((f) => ({ ...f, projectName: e.target.value }))}
                                />
                                {errors.projectName && <p className="text-xs text-destructive">{errors.projectName}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="project-projectNumber">
                                    Project Number <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="project-projectNumber"
                                    value={form.projectNumber}
                                    onChange={(e) => {
                                        const projectNumber = e.target.value;
                                        // Clear a stale duplicate warning as soon as they change it.
                                        setErrors(({ projectNumber: _drop, ...rest }) => rest);
                                        setForm((f) => ({ ...f, projectNumber }));
                                    }}
                                    aria-invalid={Boolean(errors.projectNumber)}
                                />
                                {errors.projectNumber ? (
                                    <p className="text-xs text-destructive">{errors.projectNumber}</p>
                                ) : (
                                    <p className="text-xs text-muted-foreground">
                                        Must be unique — no two projects can share a number.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="project-siteAddress">
                                Site Address <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="project-siteAddress"
                                rows={2}
                                value={form.siteAddress}
                                onChange={(e) => setForm((f) => ({ ...f, siteAddress: e.target.value }))}
                            />
                            {errors.siteAddress && <p className="text-xs text-destructive">{errors.siteAddress}</p>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {comboboxField(
                                'customerId',
                                'Customer',
                                customerOptions.map((c) => ({ id: c.id, label: c.tradingName })),
                                'No active customers yet'
                            )}
                            {comboboxField(
                                'projectManagerId',
                                'Project Manager',
                                userOptions.map((u) => ({
                                    id: u.id,
                                    label: u.jobRole ? `${u.name} — ${u.jobRole}` : u.name,
                                })),
                                'No people available'
                            )}
                            {comboboxField(
                                'branchId',
                                'Branch',
                                branchOptions.map((b) => ({ id: b.id, label: b.name })),
                                'No active branches yet'
                            )}

                            {comboboxField(
                                'yardId',
                                'Yard',
                                yardOptions.map((y) => ({ id: y.id, label: `${y.yardName} (${y.yardCode})` })),
                                'No open yards yet'
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} className="gap-2">
                            {saving && <Loader2 size={16} className="animate-spin" />}
                            {project ? 'Save Changes' : 'Add Project'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectFormDialog;
